import { useState } from "react";
import { useOffersStore } from "../../../stores/offersStore";
import { XIcon, ChevronLeftIcon, ChevronRightIcon, EditIcon, Trash2Icon } from "lucide-react";
import { DialogHeader, DialogFooter, Dialog, DialogContent, DialogTitle } from "../../../components/ui/dialog";
import {
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerClose,
  DrawerFooter,
  Drawer,
} from "@/src/components/ui/drawer";
import { Separator } from "@/src/components/ui/separator";
import { Button } from "@/src/components/ui/button";
import { OfferYacht } from "../model";
import { YachtOfferCard } from "./YachtOfferCard";
import { YachtDetailsModal } from "./YachtDetailsModal";
import { UseFormSetValue } from "react-hook-form";
import { OfferForm } from "./offer.schema";
import { Currency } from "../../Currencies/model";

export const YachtsModal = ({
  open,
  setOpen,
  setValue,
  currency,
  editYacht,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  setValue: UseFormSetValue<OfferForm>;
  currency: Currency["code"];
  editYacht: (id: string, index: number) => void;
}) => {
  const [yachtDetailsOpen, setYachtDetailsOpen] = useState(false);
  const [removeYachtOpen, setRemoveYachtOpen] = useState(false);
  const [yachtId, setYachtId] = useState<string | null>(null);
  const [yachtIndex, setYachtIndex] = useState<number | null>(null);
  const { offerYachts, setOfferYachts } = useOffersStore();

  const onDelete = () => {
    if (yachtIndex === null) return false;
    const filteredYachts = [...offerYachts]?.filter(({ yacht }, i) => i !== yachtIndex);
    setOfferYachts(filteredYachts);
    setYachtIndex(null);
    setRemoveYachtOpen(false);
    !filteredYachts?.length && setOpen(false);
    setValue("yachts", filteredYachts);
  };

  const removeYachtModal = (index: number) => {
    setYachtIndex(index);
    setRemoveYachtOpen(true);
  };

  const handleYachtDetailsModal = (id: string) => {
    setYachtId(id);
    setYachtDetailsOpen(true);
  };

  return (
    <>
      <Drawer onOpenChange={setOpen} direction="right" open={open}>
        <DrawerOverlay className="fixed inset-0 !bg-slate-900/60" />
        <DrawerContent className="h-full w-[84%] left-[unset] rounded-none bg-white">
          <DrawerHeader className="pt-8 pb-6 px-12 flex items-center justify-between">
            <DialogTitle className="text-3xl font-medium">Yachts added to the offer</DialogTitle>
            <DrawerClose className="cursor-pointer" asChild>
              <XIcon size={32} />
            </DrawerClose>
          </DrawerHeader>
          <div className="px-6 overflow-y-auto">
            <Separator />
            <div className="flex flex-col gap-4 py-6">
              {[...offerYachts]
                .sort((a: any, b: any) => a?.order - b?.order)
                .map((offerYacht: OfferYacht, index) => (
                  <div className="flex flex-col p-4 border rounded-lg border-blue-100">
                    <YachtOfferCard
                      {...offerYacht}
                      key={index}
                      header={
                        <ActionButtons
                          onEdit={() => editYacht(offerYacht.yacht.id, index)}
                          onDelete={() => removeYachtModal(index)}
                        />
                      }
                      openYachtDetails={() => handleYachtDetailsModal(offerYacht.yacht.id)}
                      currency={currency}
                    />
                  </div>
                ))}
            </div>
          </div>
          <DrawerFooter className="flex justify-between items-center shadow-minusTop flex-row">
            <Button onClick={() => setOpen(false)} variant="outline">
              <ChevronLeftIcon size={16} />
              Back
            </Button>
            <Button onClick={() => setOpen(false)} variant="default">
              Next
              <ChevronRightIcon size={16} />
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
      <Dialog open={!!removeYachtOpen} onOpenChange={setRemoveYachtOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-xl font-medium">Remove a yacht from the offer</DialogTitle>
            <p className="mt-2 text-slate-500">
              You are removing a yacht from the offer. Your changes cannot be undone.
            </p>
          </DialogHeader>
          <DialogFooter className="flex mt-8 w-full !justify-between">
            <Button onClick={() => setYachtIndex(null)} variant="outline">
              Cancel
            </Button>
            <Button onClick={onDelete}>Remove this yacht</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {yachtDetailsOpen && (
        <YachtDetailsModal yachtDetailsId={yachtId} open={yachtDetailsOpen} setOpen={setYachtDetailsOpen} />
      )}
    </>
  );
};

const ActionButtons = ({ onDelete, onEdit }: { onDelete: () => void; onEdit: () => void }) => {
  return (
    <div className="w-full flex flex-col pb-4 mb-4 gap-4">
      <div className="flex justify-end">
        <Button
          onClick={(e) => {
            e.preventDefault();
            onEdit();
          }}
          className="text-black gap-2"
          variant="link"
        >
          <EditIcon />
          Edit details
        </Button>
        <Button
          onClick={(e) => {
            e.preventDefault();
            onDelete();
          }}
          className="gap-2"
          variant="destructive"
        >
          <Trash2Icon />
          Remove
        </Button>
      </div>
      <Separator />
    </div>
  );
};
